import styled, { keyframes } from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../../../styles/authShellMixins";

// Animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Container principal
export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div`
  scrollbar-width: none; 
  overflow-y: auto; 
  overflow-x: hidden; 
  position: relative; 
  height: 100vh; 
  min-height: 100vh; 
  width: ${({ collapsed }) => (collapsed ? "calc(100% - 6vw)" : "calc(100% - 15vw)")};
  margin: auto;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out; 
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  ${mobileAuthSideContainer}
`;

// Wrapper do conteúdo
export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  padding: 2rem 2rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    padding: 1.25rem 1rem 2.5rem;
  }
`;

// Padrão de fundo decorativo
export const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  background-image: 
    radial-gradient(circle at 25% 25%, var(--accent-secondary) 2px, transparent 2px),
    radial-gradient(circle at 75% 75%, var(--accent) 1px, transparent 1px);
  background-size: 100px 100px, 60px 60px;
  z-index: 0;
`;

// Seção do cabeçalho
export const HeaderSection = styled.header`
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
`;

// Título principal
export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 2rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Subtítulo
export const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--text-heading);
  margin: 0;
  letter-spacing: 0.05em;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

// Botão estilizado
export const StyledButton = styled.button`
  background: ${props => props.secondary 
    ? 'var(--input-bg)' 
    : 'var(--accent-secondary-soft)'};
  border: 2px solid ${props => props.secondary 
    ? 'var(--border-subtle)' 
    : 'var(--border-default)'};
  border-radius: 16px;
  color: ${props => props.secondary 
    ? 'var(--text-primary)' 
    : 'var(--text-heading)'};
  cursor: pointer;
  padding: 12px 24px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  min-width: 140px;
  min-height: 44px;
  z-index: 2;
  margin: 0;
  flex: 0 1 auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      ${props => props.secondary 
        ? 'var(--input-bg) 0%, var(--surface-glass) 100%' 
        : 'var(--accent-secondary-soft) 0%, var(--accent-soft) 100%'}
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 14px;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: transparent;
    box-shadow: ${props => props.secondary 
      ? 'var(--shadow-sm)' 
      : '0 6px 15px var(--accent-soft)'};
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.secondary 
      ? 'var(--text-muted)' 
      : 'var(--accent)'};
    color: var(--text-primary);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media screen and (max-width: 768px) {
    min-width: 0;
    width: 100%;
    padding: 12px 20px;
  }
`;

export const ButtonContainerLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 0.5%;
  gap: 0.5rem;
`;

// Card de informações do usuário
export const UserInfoCard = styled.div`
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 800px;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
`;

// Card de configurações
export const SettingsCard = styled.div`
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 800px;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.3s;
  animation-fill-mode: both;
`;

// Container de botões
export const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 0 2.5rem;
  padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
  box-sizing: border-box;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.4s;
  animation-fill-mode: both;

  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;
    justify-content: stretch;
    gap: 0.65rem;
    margin-bottom: 2rem;
  }
`;

// Texto informativo
export const InfoText = styled.p`
  color: var(--text-heading);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0.5rem 0;
`;

// Label estilizada
export const StyledLabel = styled.label`
  color: var(--text-heading);
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
`;

// Campo de entrada estilizado
export const StyledInput = styled.input`
  background: var(--input-bg);
  border: 1px solid var(--border-default);
  border-radius: 10px;
  color: var(--text-heading);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  width: 100%;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-soft);
  }
`;

// Select estilizado
export const StyledSelect = styled.select`
  background: var(--input-bg);
  border: 1px solid var(--border-default);
  border-radius: 10px;
  color: var(--text-heading);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  width: 100%;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-soft);
  }
  
  option {
    background: var(--surface-1);
    color: var(--text-heading);
  }
`;


export const Row = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  width: 80%;
  position: relative; 
  z-index: 2;
  margin: auto;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const LeftColumn = styled.div`
  flex: 1;
  min-width: 4vw;
  position: relative;
  z-index: 2;
  margin: auto;
`;

export const RightColumn = styled.div`
  flex: 2;
  min-width: 4vw;
  position: relative;
  z-index: 2;
  margin: auto;
`;

export const Label = styled.label`
  color: var(--text-heading);
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 0.5rem;
  display: block;
  position: relative;
  z-index: 2;
`;

// Container para tags selecionadas
export const TagsContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// Tag individual
export const Tag = styled.div`
  background: var(--accent-secondary-soft);
  border: 1px solid var(--border-default);
  color: var(--text-heading);
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
`;

// Botão de remover tag
export const RemoveTagButton = styled.span`
  margin-left: 8px;
  cursor: pointer;
  font-weight: bold;
  color: var(--danger);
  
  &:hover {
    color: var(--danger);
  }
`;